<template>
  <template v-for="(singleNumberList, msgrName, index) in data" :key="msgrName">
    <HeaderTableBlock
      :title="$t('common.numbers')"
      :countTitle="
        paginationAllMsgrs[msgrName][type === 'visible' ? 0 : 1]?.total
      "
      :messenger="msgrName"
      :loading="loadingState[msgrName]"
      :sendInput="inputValueState[msgrName]"
      @submitInput="submitInput($event, msgrName)"
    />
    <!-- Desktop or Mobile Components  -->
    <component
      :is="tableState"
      :tableTitle="msgrName"
      :numberDataList="singleNumberList"
      :tableType="type"
      :tableStatus="statusDataState?.[type][msgrName]"
      @loadMore="loadMore(msgrName)"
    >
      <template #status="{ number: numberData }">
        <button
          class="text-uppercase"
          style="text-wrap: balance"
          @click="
            onStatus({
              msgr: msgrName,
              number: numberData.number,
              crmSystem: numberData.integration,
            })
          "
        >
          {{
            $t(
              `page.integration.numbers_table.connected_numbers.${
                statusNumber(numberData)?.status
              }`
            )
          }}
        </button>
      </template>
      <template #settings="{ number: numberData }">
        <div
          class="inside_block"
          @click="onNumberSettings(numberData, msgrName)"
        >
          <BIconGear class="icon" />
        </div>
      </template>
      <!-- actions -->
      <template #actions="{ number: numberData }">
        <ButtonLoading
          variant="outline"
          class="text-uppercase"
          :loading="isLoadingButton(msgrName, numberData.number, type)"
          @click="onNumberAction(numberData, msgrName)"
        >
          {{
            $t(
              `page.integration.numbers_table.connected_numbers.${
                statusNumber(numberData)?.buttonText
              }`
            )
          }}
        </ButtonLoading>
      </template>
      <!-- PAGINATION IN SMALL AND MEDIUM SCREENS -->
      <template #pagination>
        <BasePagination
          v-show="!statusDataState?.[type][msgrName]"
          :lastPage="
            paginationAllMsgrs[msgrName][type === 'visible' ? 0 : 1]?.last_page
          "
          :currentPage="
            paginationAllMsgrs[msgrName][type === 'visible' ? 0 : 1]
              ?.current_page
          "
          @onChangePage="(numberPage) => changePage(numberPage, msgrName)"
        />
      </template>
    </component>
    <hr class="mb-4" v-show="index !== Object.keys(data).length - 1" />
  </template>
</template>

<script setup>
import { ref, computed, watchEffect, onMounted } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
// COMPONENTS
import HeaderTableBlock from "@/components/ui/table/HeaderTableBlock.vue";
import ButtonLoading from "@/components/Recurring/ButtonLoading.vue";
import DesktopNumbersTableIntegration from "@/components/Integration/DesktopNumbersTableIntegration.vue";
import MobileNumbersTableIntegration from "@/components/Integration/MobileNumbersTableIntegration.vue";
import BasePagination from "@/components/ui/BasePagination.vue";
// CONFIGS
import statusNumberConfig from "./configs/statusNumberConfig";

import { toUpperCaseFirstLetter } from "@/helpers/textTransform";

const store = useStore();
const router = useRouter();

const windowSize = ref(window.innerWidth);

const loadingButtonConfig = ref({
  messenger: "",
  number: "",
  visibility: null,
  isLoading: false,
});

const paginationAllMsgrs = computed(() => {
  return store.state.numbers.numbersDataAttributes?.numberListPagination;
});

const props = defineProps({
  data: {
    type: Object, // {viber:[], telegram:[]}
    default: () => ({}),
  },
  //   ! rename type => visibility
  type: {
    type: String, // 'visible' || 'invisible'
  },
});

const emits = defineEmits(["onSearchValue"]);

const isLastPage = (paginationData) => {
  return paginationData
    ? paginationData.current_page === paginationData.last_page
    : true;
};

/** Updates the loading button configuration.
 *
 * @param {string} msgr
 * @param {boolean|null} visibility
 * @param {boolean} isLoading
 * @return {void}
 */
const onLoadingButton = (msgr, number, visibility, isLoading) => {
  loadingButtonConfig.value = {
    messenger: msgr,
    number,
    visibility,
    isLoading,
  };
};

const isLoadingButton = (msgr, number, visibleTable) => {
  const visibility = visibleTable === "visible" ? true : false;
  const validatedNumber = [
    loadingButtonConfig.value.number === number,
    loadingButtonConfig.value.messenger === msgr,
    loadingButtonConfig.value.visibility === visibility,
    loadingButtonConfig.value.isLoading,
  ];
  const isLoading = !validatedNumber.includes(false);
  return isLoading;
};

const submitInput = (input, msgr) => {
  emits("onSearchValue", { input, msgr });
};

const tableState = computed(() =>
  windowSize.value >= 1200
    ? DesktopNumbersTableIntegration
    : MobileNumbersTableIntegration
);

const statusDataState = computed(() => {
  return {
    visible: {
      viber: setDataStatusInTable("visible", "viber"),
      telegram: setDataStatusInTable("visible", "telegram"),
      whatsapp: setDataStatusInTable("visible", "whatsapp"),
    },
    invisible: {
      viber: setDataStatusInTable("invisible", "viber"),
      telegram: setDataStatusInTable("invisible", "telegram"),
      whatsapp: setDataStatusInTable("invisible", "whatsapp"),
    },
  };
});

const loadingState = computed(() => {
  const visibilityIndexNumber = props.type === "visible" ? 0 : 1;
  const { viber, telegram, whatsapp } =
    store.state.numbers.numbersDataAttributes.loading;
  return {
    viber: viber[visibilityIndexNumber],
    telegram: telegram[visibilityIndexNumber],
    whatsapp: whatsapp[visibilityIndexNumber],
  };
});

const inputValueState = computed(() => {
  const visibilityIndexNumber = props.type === "visible" ? 0 : 1;
  const { viber, telegram, whatsapp } =
    store.state.numbers.numbersDataAttributes.searchInput;
  return {
    viber: viber[visibilityIndexNumber],
    telegram: telegram[visibilityIndexNumber],
    whatsapp: whatsapp[visibilityIndexNumber],
  };
});

const loadMore = (messenger) => {
  const visibility = props.type === "visible" ? true : false;
  const number =
    store.state.numbers.numbersDataAttributes.searchInput[messenger][
      visibility ? 0 : 1
    ] || null;

  const pageData =
    paginationAllMsgrs.value[messenger][props.type === "visible" ? 0 : 1];
  const isLastPage = pageData.current_page === pageData.last_page;
  const page = !isLastPage && pageData.current_page + 1;

  // !remove
  const visibilityNumber = toUpperCaseFirstLetter(props.type);
  const msgrUppercase = toUpperCaseFirstLetter(messenger);
  const getNumberData = `numbers/get${visibilityNumber}Numbers${msgrUppercase}`;

  // если не последняя страница, шлем запрос
  if (!isLastPage) {
    store.dispatch("numbers/loadNumbersByMessenger", {
      messenger,
      number,
      page,
      visibility,
    });
  }
};

const changePage = (numberPage, messenger) => {
  const visibility = props.type === "visible" ? true : false;

  const number =
    store.state.numbers.numbersDataAttributes.searchInput[messenger][
      visibility ? 0 : 1
    ] || null;

  store.dispatch("numbers/loadNumbersByMessenger", {
    messenger,
    number,
    page: numberPage,
    visibility,
    isCollectData: false, // предыдущие данные не выводяться(постраничная пагинация)
  });
};

watchEffect(() => {
  window.addEventListener("resize", () => {
    windowSize.value = window.innerWidth;
  });
});

/**
 * Returns the status of a messenger based on the visibility of its numbers.
 *
 * @param {string} visibility ('visible' || 'invisible') - The visibility number(props.type).
 * @param {string} msgr - The messenger name.
 * @return {string} status - "loading"(грузяться данные), "noNumbers"(нет номеров), null(номера есть).
 */
const setDataStatusInTable = (visibility, msgr) => {
  let status;
  const hasData = !!props.data[msgr]?.length;

  const uppercaseVisibility = toUpperCaseFirstLetter(visibility);
  const uppercaseMsgr = toUpperCaseFirstLetter(msgr);
  const isLoading =
    store.getters[
      `numbers/get${uppercaseVisibility}NumbersDataLoading${uppercaseMsgr}`
    ];

  switch (true) {
    case isLoading:
      status = "loading";
      break;
    case !hasData:
      status = "noNumbers";
      break;
    default:
      status = null;
      break;
  }
  return status;
};

const statusNumber = (numberData) => {
  const { status, buttonText } = statusNumberConfig(numberData);
  return { status, buttonText };
};

const onStatus = ({ msgr, number, crmSystem }) => {
  store.commit("setNumberData", {
    number: number,
  });

  const numberData = props.data[msgr].find((item) => item.number === number);
  const { status, buttonText } = statusNumberConfig(numberData);

  switch (status) {
    case "active":
      // store.commit("openDialog", { name: 'InformationDialog', info: 'numberActivated', infoIcon: true });
      break;
    case "active_pre":
      // store.commit("openDialog", { name: 'InformationDialog', info: 'numberActivatedAvans', infoIcon: true });
      break;
    case "waiting_for": // 'Не подключен к СРМ'
      store.commit("openDialog", {
        name: "WhichCrmSystemDialog",
        params: { messenger: msgr },
      });
      break;
    case "error": // 'error'
      store.commit("openDialog", "HelpDialog");
      break;
    case "requires_activation": // 'Требует активации'
      const numberPayload = {
        system: crmSystem,
        number_type: msgr,
        number,
      };

      switch (msgr) {
        case "telegram":
          router.push({
            name: "ActivationTelegram",
            params: { number: number },
          });
          break;
        case "viber":
          router.push({
            name: "ActivationViber",
            params: { number: number },
          });
          break;
        case "whatsapp":
          router.push({
            name: "ActivationWhatsapp",
            params: { number: number },
          });
          break;
        default:
          console.error("Unknown messenger", msgr);
      }
      break;
    case "number_disable": // 'Номер отключен'
      store.commit("openDialog", {
        name: "InformationDialog",
        info: "numberDisabled",
      });
      break;
  }
};

const onNumberSettings = (numberData, messenger) => {
  // ! remove?
  store.commit("setNumberData", numberData.number);

  // router.push({ params: { number: number.number } });
  store.commit("openDialog", {
    name: "SettingsNumberDialog",
    info: {
      number: numberData.number,
      visibility: numberData.visibility,
      messenger,
      webhooks: numberData.webhooks,
      api_key: numberData.api_key,
      tags: numberData.tags,
    },
  });
};

const onNumberAction = (numberData, messenger) => {
  store.commit("setNumberData", {
    number: numberData,
    number_type: messenger,
  });

  // NEW STORE
  store.commit("activation/SET_NUMBER", numberData.number);
  // store.commit('activation/SET_INTEGRATION', numberData.integration);
  store.commit("activation/SET_MESSENGER", messenger);

  const numberPayload = {
    number: numberData,
    number_type: messenger,
    system: numberData.integration,
  };
  const nameActionButton = statusNumber(numberData)?.buttonText;

  switch (nameActionButton) {
    case "pay_button":
      router.push({ name: "Tariffs" });
      break;
    case "activation_button":
      switch (messenger) {
        case "telegram":
          router.push({
            name: "ActivationTelegram",
            params: { number: numberData.number },
          });
          break;
        case "viber":
          router.push({
            name: "ActivationViber",
            params: { number: numberData.number },
          });
          break;
        case "whatsapp":
          router.push({
            name: "ActivationWhatsapp",
            params: { number: numberData.number },
          });
          break;
        default:
          console.error("Unknown messenger: ", messenger);
      }
      break;
    case "return":
      const visibilityPayload = {
        number: numberData.number,
        visibility: true,
        messenger,
      };
      // console.log(action, visibilityPayload)
      onLoadingButton(messenger, numberData.number, false, true);
      store.dispatch("numbers/updateVisibility", visibilityPayload).then(() => {
        store.dispatch("numbers/loadNumbersByMessenger", {
          messenger,
          visibility: true,
        });
        store
          .dispatch("numbers/loadNumbersByMessenger", {
            messenger,
            visibility: false,
          })
          .then(() => {
            onLoadingButton(messenger, numberData.number, false, false);
          });
      });
      break;
    case "help_button":
    case "error":
    default:
      store.commit("openDialog", "HelpDialog");
      break;
  }
};
</script>

<style lang="scss" scoped>
@use "@/styles/table.scss";
.header_table {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 12px 6px;
  margin-left: 12px;
}
</style>
